import * as React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_WorkOrder_FindPrivateConfirmation_VM } from 'ab-api/web/workOrder/findPrivateConfirmation';

import { DOWNLOAD_ORDER_CONFIRMATION } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button, infoToast } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import Loading from 'af-components/DocumentViewer/Loading';

import type { RootState } from 'af-reducers/index';

import * as WorkOrderActions from 'af-actions/workOrder';

import WorkOrderConfirmation from '../Shared/WorkOrderConfirmation';

interface PathParams {
	workOrderId: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;
type Props = OwnProps & ConnectedProps<typeof connector>;

const ConfirmationPrivate: React.FC<Props> = (props) => {
	const {
		match: { params: { workOrderId } },
		location: { state: { orgAlias, originUrl, originLabel } },
		findForConfirmationById,
		findWorkOrderById,
		downloadWorkOrderConfirmationPdf,
		resetWorkOrder,
		companyName,
		isDownloading,
	} = props;

	const [workOrder, setWorkOrder] = React.useState<Nullable<W_WorkOrder_FindPrivateConfirmation_VM>>(null);

	React.useEffect(() => {
		const fetchData = async () => {
			await findWorkOrderById(+workOrderId);
			const _workOrder = await findForConfirmationById(+workOrderId);
			setWorkOrder(_workOrder);
		};
		fetchData();
		return () => {
			resetWorkOrder();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const downloadPdf = React.useCallback(async () => {
		if (!workOrder?.code) {
			throw new Error('Work order not defined');
		}

		infoToast(`${workOrderId}-confirmation`, { title: 'Started preparing confirmation for download...' });
		await downloadWorkOrderConfirmationPdf(workOrderId, `${workOrder?.code}_confirmation.pdf`);
	}, [downloadWorkOrderConfirmationPdf, workOrder, workOrderId]);

	if (!workOrder) {
		return (<Loading />);
	}

	const orderCode = workOrder?.code;
	const url = originUrl || CLIENT.COMPANY.SCHEDULE_BOARD.DAILY_VIEW(orgAlias, companyName);
	const _originUrl = `${url}?order=${orderCode ?? ''}`;
	const _originLabel = originLabel || 'Schedule Board';

	return (
		<div className="form-segment form-segment--midi">
			<Breadcrumbs
				items={
					[
						{ label: _originLabel, url: _originUrl },
						{ label: 'Work Order Confirmation' },
					]
				}
			/>
			<div className="form-box form-box--full-padded">
				<WorkOrderConfirmation workOrder={workOrder} />
			</div>
			<div className="btn--floating">
				<Button
					icon="UtilitiesPrint"
					onClick={downloadPdf}
					size="big"
					style="primary"
					submitting={isDownloading}
					tooltip="Print"
					type="button"
				/>
			</div>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		isDownloading: state.http.submitting.includes(DOWNLOAD_ORDER_CONFIRMATION),
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findWorkOrderById: WorkOrderActions.findWorkOrderById,
		findForConfirmationById: WorkOrderActions.findForConfirmationById,
		downloadWorkOrderConfirmationPdf: WorkOrderActions.downloadWorkOrderConfirmationPdf,
		resetWorkOrder: WorkOrderActions.resetWorkOrder,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(ConfirmationPrivate);
